import { memo, useState, forwardRef } from "react";
import { usePlayerName } from "@/hooks/usePlayerName";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { RefreshCw } from "lucide-react";

interface PlayerNameProps {
  uuid: string;
  className?: string;
}

// Button component that can accept refs for Tooltip
const RetryButton = forwardRef<HTMLButtonElement, {
  onClick: (e: React.MouseEvent) => void;
  disabled: boolean;
  showLoading: boolean;
  className?: string;
}>(({ onClick, disabled, showLoading, className }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity disabled:cursor-wait ${className || ''}`}
  >
    <Skeleton className="h-4 w-16" />
    <RefreshCw className={`h-3 w-3 text-muted-foreground ${showLoading ? 'animate-spin' : ''}`} />
  </button>
));

RetryButton.displayName = 'RetryButton';

const PlayerName = memo(({ uuid, className }: PlayerNameProps) => {
  const { data: name, isLoading, refetch, isFetching } = usePlayerName(uuid);
  const [isRetrying, setIsRetrying] = useState(false);
  
  const handleRetry = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsRetrying(true);
    try {
      await refetch();
    } catch (error) {
      console.error("Failed to refetch player name:", error);
    } finally {
      setIsRetrying(false);
    }
  };
  
  const showLoading = isLoading || isFetching || isRetrying;
  const hasFailed = !showLoading && !name;
  
  // Show clickable skeleton while loading OR if name couldn't be fetched (null)
  if (showLoading || !name) {
    if (hasFailed) {
      return (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <RetryButton 
              onClick={handleRetry} 
              disabled={showLoading} 
              showLoading={showLoading} 
              className={className} 
            />
          </TooltipTrigger>
          <TooltipContent>
            <p>Name konnte nicht geladen werden – klicken zum erneuten Laden</p>
          </TooltipContent>
        </Tooltip>
      );
    }
    
    return (
      <RetryButton 
        onClick={handleRetry} 
        disabled={showLoading} 
        showLoading={showLoading} 
        className={className} 
      />
    );
  }
  
  // Ensure name is a string before rendering
  const displayName = typeof name === 'string' ? name : String(name || '');
  
  return <span className={className}>{displayName}</span>;
});

PlayerName.displayName = 'PlayerName';

export default PlayerName;
